import { Link } from 'react-router-dom';
import { Github, Twitter, Instagram, Linkedin, Mail, ArrowUpRight } from 'lucide-react';
import BrandLogo from './BrandLogo';

const LINKS = {
  Product: [
    { label: 'AI Builder', to: '/build' },
    { label: 'SOUL Library', to: '/souls' },
    { label: 'Free tools', to: '/#templates' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Roadmap', to: '/about#roadmap' },
  ],
  Templates: [
    { label: 'Flight Itinerary', to: '/templates/flight-itinerary' },
    { label: 'Holiday Planner', to: '/templates/holiday-planner' },
    { label: 'Invoice Generator', to: '/templates/invoice' },
    { label: 'QR Code Maker', to: '/templates/qr-code' },
    { label: 'Resume Builder', to: '/templates/resume' },
    { label: 'PDF Editor', to: '/templates/pdf-editor' },
  ],
  Company: [
    { label: 'About', to: '/about' },
    { label: 'Contact', to: '/contact' },
    { label: 'Blog', to: '/blog', soon: true },
    { label: 'Careers', to: '/careers', soon: true },
  ],
  Legal: [
    { label: 'Terms of Service', to: '/terms' },
    { label: 'Privacy Policy', to: '/privacy' },
    { label: 'Cookies', to: '/privacy#cookies' },
    { label: 'DPA', to: '/privacy#dpa' },
  ],
};

const SOCIALS = [
  { icon: Twitter, href: 'https://twitter.com/', label: 'Twitter' },
  { icon: Instagram, href: 'https://instagram.com/', label: 'Instagram' },
  { icon: Linkedin, href: 'https://linkedin.com/', label: 'LinkedIn' },
  { icon: Github, href: 'https://github.com/', label: 'GitHub' },
];

export default function SiteFooter() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#020202] text-white border-t border-white/[0.06] overflow-hidden"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 py-16">
        {/* Top row: brand + newsletter */}
        <div className="grid md:grid-cols-2 gap-10 pb-12 border-b border-white/[0.06]">
          <div>
            <BrandLogo size={44} variant="stacked" showTagline />
            <p className="mt-4 text-[13.5px] text-white/60 max-w-md leading-relaxed">
              An AI-first launch platform. Ship apps, sites, and tools in minutes — powered by
              Claude, GPT-5, and Gemini. Made for builders, agencies, and dreamers around the world.
            </p>
            <div className="mt-5 flex items-center gap-2">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                    className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] flex items-center justify-center text-white/60 hover:text-white transition"
                  >
                    <Icon size={14} />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="md:pl-8 md:border-l border-white/[0.06]">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-2">
              Stay in the loop
            </div>
            <h3 className="text-xl md:text-2xl font-display font-black tracking-tight">
              Get product updates + free launch playbooks
            </h3>
            <p className="mt-2 text-[13px] text-white/55">
              One email per week. No spam. Unsubscribe anytime.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const email = new FormData(e.currentTarget).get('email');
                if (!email) return;
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email, source: 'newsletter' }),
                }).catch(() => {});
                e.currentTarget.reset();
                e.currentTarget.querySelector('button').innerText = 'Subscribed ✓';
              }}
              className="mt-4 flex gap-2"
              data-testid="footer-newsletter-form"
            >
              <input
                name="email"
                type="email"
                required
                placeholder="you@company.com"
                data-testid="footer-newsletter-email"
                className="flex-1 h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="h-11 px-5 rounded-xl text-black text-sm font-semibold hover:scale-[1.02] transition"
                style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-12">
          {Object.entries(LINKS).map(([heading, links]) => (
            <div key={heading}>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">
                {heading}
              </div>
              <ul className="space-y-2">
                {links.map((l) => (
                  <li key={l.label}>
                    {l.soon ? (
                      <span
                        className="text-[13.5px] text-white/40 inline-flex items-center gap-1.5"
                        title="Coming soon"
                      >
                        {l.label}
                        <span className="text-[9px] font-mono uppercase tracking-widest border border-white/10 rounded-full px-1.5 py-0 text-white/40">
                          Soon
                        </span>
                      </span>
                    ) : (
                      <Link
                        to={l.to}
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className="text-[13.5px] text-white/70 hover:text-white transition inline-flex items-center gap-1 group"
                      >
                        {l.label}
                        <ArrowUpRight
                          size={11}
                          className="text-white/30 opacity-0 group-hover:opacity-100 transition"
                        />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/[0.06] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-[12px] text-white/40">
            © {new Date().getFullYear()} Around the World AI. All rights reserved.
          </div>
          <div className="flex items-center gap-4">
            <a
              href="mailto:hello@aroundtheworldai.com"
              className="text-[12px] text-white/50 hover:text-white flex items-center gap-1.5 transition"
              data-testid="footer-contact-email"
            >
              <Mail size={12} /> hello@aroundtheworldai.com
            </a>
            <span className="hidden md:inline h-3 w-px bg-white/10" />
            <div className="flex items-center gap-2 text-[11px]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-white/50 font-mono">All systems operational</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
